/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import { Modal, ModalBody, ModalHeader } from "baseui/modal";
import * as React from "react";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";
import { LabelLarge, LabelMedium } from "baseui/typography";
import { Input } from "baseui/input";
import { Button } from "baseui/button";
import {
  isPasswordLongEnough,
  MIN_PASSWORD_LENGTH,
} from "../Utils/AuthHelpers";
import { notify } from "./Notify";
import { AxiosContext, DexterAuthContext } from "../Utils/AuthContext";
import { notifyGeneralError } from "./Errors";
import { ChangePasswordRequest } from "Api";
import { Endpoints } from "../Utils/Common";
import { Hide, Show } from "baseui/icon";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import { getTooltipOverrides } from "../DesignSystem/TooltipStyles";

export interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  showMustChangeMessage?: boolean;
}

// Updated show/hide icons as custom components for tooltip consistency
const MaskToggleShowIconNoTitle = React.forwardRef<
  SVGSVGElement,
  React.ComponentProps<typeof Show>
>((props, ref) => {
  const { currentTheme } = useDexterTheme();
  return (
    <StatefulTooltip
      placement={PLACEMENT.right}
      content={"Show Password Text"}
      overrides={getTooltipOverrides(currentTheme)}
    >
      <Show {...props} ref={ref} title="" />
    </StatefulTooltip>
  );
});

MaskToggleShowIconNoTitle.displayName = "MaskToggleShowIconNoTitle";

const MaskToggleHideIconNoTitle = React.forwardRef<
  SVGSVGElement,
  React.ComponentProps<typeof Hide>
>((props, ref) => {
  const { currentTheme } = useDexterTheme();
  return (
    <StatefulTooltip
      placement={PLACEMENT.right}
      content={"Hide Password Text"}
      overrides={getTooltipOverrides(currentTheme)}
    >
      <Hide {...props} ref={ref} title="" />
    </StatefulTooltip>
  );
});

MaskToggleHideIconNoTitle.displayName = "MaskToggleHideIconNoTitle";

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onClose,
  showMustChangeMessage,
}) => {
  const axios = React.useContext(AxiosContext);
  const dexterAuth = React.useContext(DexterAuthContext);
  /**
   * Change the currently logged in user's password.
   * On success the calling modal will close; on failure
   * the server error is surfaced via the existing notification path.
   */
  const changePassword = async (
    oldPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<boolean> => {
    const changePasswordRequest: ChangePasswordRequest = {
      oldPassword: oldPassword,
      newPassword: newPassword,
      confirmPassword: confirmPassword,
    };
    try {
      await axios.post(Endpoints.ChangePassword, changePasswordRequest);
      return true;
    } catch (error) {
      if (error.response.status == 401) {
        // Authentication expired, clear the bad token so they can re-authenticate
        // and not get soft-locked by the modal
        await dexterAuth.logoutFromServer();
        notifyGeneralError(
          error,
          "Password change unsuccessful. Please re-authenticate"
        );
        onClose();
      } else {
        // General failure (validation or server error), stay on modal to allow for correction
        notifyGeneralError(error, "Password change unsuccessful.");
      }
      return false;
    }
  };

  const { currentTheme } = useDexterTheme();
  const [oldPassword, setOldPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Track which fields the user has left (blurred). Validation hints only show
  // after a field has been touched, so we don't nag while they're still typing.
  const [touched, setTouched] = React.useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const markTouched = (
    field: "oldPassword" | "newPassword" | "confirmPassword"
  ): void => setTouched((prev) => ({ ...prev, [field]: true }));

  const passwordLongEnough = isPasswordLongEnough(newPassword);
  const passwordsMatch =
    newPassword === confirmPassword && newPassword.length > 0;
  const noEmptyFields =
    oldPassword.trim() !== "" && newPassword !== "" && confirmPassword !== "";

  const canSubmit = noEmptyFields && passwordLongEnough && passwordsMatch;

  const handleCancel = (): void => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    onClose();
  };

  const handleChangePassword = async (): Promise<void> => {
    if (!canSubmit || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    const succeeded = await changePassword(
      oldPassword,
      newPassword,
      confirmPassword
    );
    setIsSubmitting(false);

    if (succeeded) {
      notify.positive("Your password has been successfully changed.");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");
      onClose();
    }
    // On failure, changePassword already surfaced the server error via
    // notifyGeneralError; stay on the form so the user can correct and retry.
  };

  const primaryButtonOverrides = {
    BaseButton: {
      style: ({ $disabled }: { $disabled: boolean }) => ({
        backgroundColor: $disabled
          ? currentTheme.colors.buttonDisabledColor
          : currentTheme.colors.primaryButtonColor,
        color: $disabled
          ? currentTheme.colors.secondaryTextColor
          : currentTheme.colors.primaryElementFontColor,
      }),
    },
  };

  const secondaryButtonOverrides = {
    BaseButton: {
      style: {
        backgroundColor: currentTheme.colors.backgroundTertiary,
        color: currentTheme.colors.text,
      },
    },
  };

  const inputOverrides = {
    Root: {
      style: {
        backgroundColor: currentTheme.colors.dropdownBackground,
        borderTopColor: currentTheme.colors.dropdownBorderColor,
        borderBottomColor: currentTheme.colors.dropdownBorderColor,
        borderLeftColor: currentTheme.colors.dropdownBorderColor,
        borderRightColor: currentTheme.colors.dropdownBorderColor,
      },
    },
    Input: {
      style: {
        backgroundColor: currentTheme.colors.dropdownBackground,
        color: currentTheme.colors.dropdownText,
        caretColor: currentTheme.colors.dropdownText,
        "::placeholder": {
          color: currentTheme.colors.secondaryTextColor,
        },
      },
    },
    InputContainer: {
      style: {
        backgroundColor: currentTheme.colors.dropdownBackground,
      },
    },
    EndEnhancer: {
      style: {
        backgroundColor: currentTheme.colors.dropdownBackground,
      },
    },
    MaskToggleButton: {
      props: { title: null },
      style: ({ $isHovered }: { $isHovered: boolean }) => ({
        backgroundColor: $isHovered
          ? currentTheme.colors.dropdownItemHoverBackground
          : currentTheme.colors.dropdownBackground,
        color: currentTheme.colors.dropdownText,
      }),
    },
    MaskToggleShowIcon: MaskToggleShowIconNoTitle,
    MaskToggleHideIcon: MaskToggleHideIconNoTitle,
  };

  const showPasswordLengthHint =
    touched.newPassword && newPassword.length > 0 && !passwordLongEnough;
  const showMismatchHint =
    touched.confirmPassword && confirmPassword.length > 0 && !passwordsMatch;

  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      closeable
      animate
      autoFocus
      overrides={{
        Dialog: {
          style: {
            backgroundColor: currentTheme.colors.pageBackgroundColor,
            width: "30vw",
            maxWidth: "800px",
            height: "60vh",
            display: "flex",
            flexDirection: "column",
          },
        },
        Close: {
          style: {
            color: currentTheme.colors.text,
          },
        },
      }}
    >
      <ModalHeader
        style={{
          color: currentTheme.colors.fileModalHeaderTextColor,
          fontFamily: currentTheme.fonts.primary,
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}
      >
        <span>{"Change Password"}</span>
      </ModalHeader>
      <ModalBody>
        <LabelMedium style={{ paddingBottom: "1rem" }}>
          {showMustChangeMessage
            ? "Your password has expired and must be changed"
            : "Fill in the below fields to change your password"}
        </LabelMedium>
        <div
          style={{
            width: "100%",
          }}
        >
          <div style={{ paddingBottom: "1rem" }}>
            <Input
              value={oldPassword}
              type="password"
              onChange={(e) => setOldPassword(e.currentTarget.value)}
              onBlur={() => markTouched("oldPassword")}
              placeholder="Current Password"
              data-testid="pw-change-old-password"
              overrides={inputOverrides}
            />
          </div>
          <div style={{ paddingBottom: "1rem" }}>
            <Input
              value={newPassword}
              type="password"
              onChange={(e) => setNewPassword(e.currentTarget.value)}
              onBlur={() => markTouched("newPassword")}
              placeholder={`New Password (min ${MIN_PASSWORD_LENGTH} characters)`}
              data-testid="pw-change-new-password"
              overrides={inputOverrides}
            />
          </div>
          <div style={{ paddingBottom: "1rem" }}>
            <Input
              value={confirmPassword}
              type="password"
              onChange={(e) => setConfirmPassword(e.currentTarget.value)}
              onBlur={() => markTouched("confirmPassword")}
              placeholder="Confirm password"
              data-testid="pw-change-confirm-password"
              overrides={inputOverrides}
              onKeyDown={(e) => e.key === "Enter" && handleChangePassword()}
            />
          </div>
          <div style={{ minHeight: "1.5rem", paddingTop: "0.25rem" }}>
            {showPasswordLengthHint && (
              <LabelLarge color={currentTheme.colors.errorTextColor}>
                {"Password must be at least 12 characters."}
              </LabelLarge>
            )}
            {showMismatchHint && (
              <LabelLarge color={currentTheme.colors.errorTextColor}>
                {"Passwords do not match."}
              </LabelLarge>
            )}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: "1rem",
            paddingTop: "1rem",
            justifyContent: "flex-end",
          }}
        >
          <Button
            kind="secondary"
            data-testid="register-back-button"
            onClick={handleCancel}
            overrides={secondaryButtonOverrides}
          >
            Cancel
          </Button>
          <Button
            disabled={!canSubmit || isSubmitting}
            data-testid="register-submit-button"
            onClick={handleChangePassword}
            overrides={primaryButtonOverrides}
          >
            Change Password
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};
